import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        setError('Admin access required');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="pt-20 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-12 h-12 mx-auto mb-6 border border-[#DDD7CE] flex items-center justify-center">
            <Lock size={20} className="text-[#C6A978]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-light tracking-tighter text-[#1E1C1A] mb-2">Admin Access</h1>
          <p className="text-sm font-light text-[#7A7369]">Sign in to manage your products</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="bg-[#E8E1D8] p-8 space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-2 block">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="bg-[#F6F3EE] border-[#DDD7CE] focus:border-[#C6A978] text-[#1E1C1A] font-light"
              placeholder="admin@kiraq.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-2 block">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="bg-[#F6F3EE] border-[#DDD7CE] focus:border-[#C6A978] text-[#1E1C1A] font-light"
              placeholder="Enter password"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-[#1E1C1A] text-[#F6F3EE] py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#1E1C1A] transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </main>
  );
}
